import { Badge, Button, IconButton, Price, Select, Tabs } from '@artosera/ui';
import { formatDate } from '@artosera/shared';
import { ChevronLeft, ChevronRight, FileText, Plus, Search } from 'lucide-react';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { ContactPresente } from '../../api/contacts.js';
import { estApiError } from '../../api/errors.js';
import { useFactures, type EtatFacture, type FiltresFactures } from '../../api/ventes.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { RechercheContact } from './RechercheContact.js';
import { NouvelleFactureDialog } from './NouvelleFactureDialog.js';
import { ReglagesFacturationTab } from './ReglagesFacturationTab.js';
import { ETATS_FACTURE_TRIES, teinteDeLEtatFacture } from './etatUi.js';
import { BasculeFiltres, filtresRepliables } from '../filtres/BasculeFiltres.js';
import styles from './VentesPage.module.css';
import { Verrouillable } from '../module/LectureSeule.js';

const TRIS = ['recent', 'ancien', 'numero', 'montant'] as const;

/**
 * Ventes — liste des factures et avoirs (3.3, écran 5 ; § 17.1). Filtres
 * état / année / contact, tri, pagination ; l'onglet « Réglages de
 * facturation » n'apparaît que si le compte a l'une des actions
 * `settings:tax` ou `settings:templates` (5.1 : Direction, ou droit fin).
 */
export function VentesPage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'ventes', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const navigate = useNavigate();
  const { galerieSlug, galerie } = useShellContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [etat, setEtat] = useState('');
  const [annee, setAnnee] = useState('');
  const [contactFiltre, setContactFiltre] = useState<ContactPresente | null>(null);
  const [q, setQ] = useState('');
  const [tri, setTri] = useState<FiltresFactures['tri']>('recent');
  const [page, setPage] = useState(1);
  const [filtresOuverts, setFiltresOuverts] = useState(false);

  const filtres: FiltresFactures = useMemo(
    () => ({
      ...(etat ? { etat: [etat as EtatFacture] } : {}),
      ...(annee ? { annee: Number(annee) } : {}),
      ...(contactFiltre ? { contactId: contactFiltre.id } : {}),
      ...(q ? { q } : {}),
      tri,
      page,
      parPage: 50,
    }),
    [etat, annee, contactFiltre, q, tri, page],
  );

  const filtresActifs = [etat, annee, contactFiltre ? 'contact' : ''].filter(Boolean).length;

  const liste = useFactures(galerieSlug, filtres);
  const factures = liste.data?.factures ?? [];

  const peutFacturer = galerieALaction(galerie, 'invoices:write');
  // La recherche de contacts exige `contacts:read`, que le comptable n'a pas :
  // sans ce droit, le filtre répondrait toujours « aucun résultat ».
  const peutFiltrerParContact = galerieALaction(galerie, 'contacts:read');
  const peutReglerFacturation =
    galerieALaction(galerie, 'settings:tax') || galerieALaction(galerie, 'settings:templates');

  const nouvelleOuverte = searchParams.get('nouvelle') === '1';
  function fermerNouvelle(): void {
    const suivants = new URLSearchParams(searchParams);
    suivants.delete('nouvelle');
    setSearchParams(suivants, { replace: true });
  }

  function reinitialiserFiltres(): void {
    setEtat('');
    setAnnee('');
    setContactFiltre(null);
    setQ('');
  }

  const contenuFactures = (
    <div className={styles.page}>
      <div
        className={`${styles.filtres} ${filtresRepliables.racine}`}
        data-ouvert={filtresOuverts}
        role="search"
        aria-label={t('regie:ventes.filtres.titre')}
      >
        <div className={styles.champRecherche}>
          <Search size={16} strokeWidth={1.5} aria-hidden />
          <input
            type="search"
            className={styles.saisieRecherche}
            placeholder={t('regie:ventes.rechercheChamp')}
            aria-label={t('regie:ventes.rechercheChamp')}
            value={q}
            onChange={(event) => {
              setQ(event.target.value);
              setPage(1);
            }}
          />
        </div>

        <BasculeFiltres
          titre={t('regie:ventes.filtres.titre')}
          actifs={filtresActifs}
          ouvert={filtresOuverts}
          onBasculer={() => setFiltresOuverts((valeur) => !valeur)}
        />

        {peutFiltrerParContact ? (
          <div className={`${styles.filtreContact} ${filtresRepliables.repliable}`}>
            <RechercheContact
              galerieSlug={galerieSlug}
              contact={contactFiltre}
              onChoisir={(contact) => {
                setContactFiltre(contact);
                setPage(1);
              }}
              onEffacer={() => setContactFiltre(null)}
              flottant
            />
          </div>
        ) : null}

        <div className={`${styles.filtreEtat} ${filtresRepliables.repliable}`}>
          <Select
            aria-label={t('regie:ventes.filtres.etat')}
            value={etat}
            onValueChange={(valeur) => {
              setEtat(valeur);
              setPage(1);
            }}
            placeholder={t('regie:ventes.filtres.tousLesEtats')}
            options={[
              { value: '', label: t('regie:ventes.filtres.tousLesEtats') },
              ...ETATS_FACTURE_TRIES.map((code) => ({
                value: code,
                label: t(`ventes:factures.${code}`),
              })),
            ]}
          />
        </div>

        <input
          type="number"
          className={`${styles.saisieNombre} ${filtresRepliables.repliable}`}
          placeholder={t('regie:ventes.filtres.annee')}
          aria-label={t('regie:ventes.filtres.annee')}
          value={annee}
          onChange={(event) => {
            setAnnee(event.target.value);
            setPage(1);
          }}
        />

        <div className={`${styles.filtreTri} ${filtresRepliables.repliable}`}>
          <Select
            aria-label={t('regie:ventes.tri.libelle')}
            value={tri}
            onValueChange={(valeur) => setTri(valeur as FiltresFactures['tri'])}
            options={TRIS.map((code) => ({ value: code, label: t(`regie:ventes.tri.${code}`) }))}
          />
        </div>

        <Button
          variant="ghost"
          size="sm"
          className={`${styles.reinitialiser} ${filtresRepliables.repliable}`}
          onClick={reinitialiserFiltres}
        >
          {t('regie:ventes.filtres.reinitialiser')}
        </Button>
      </div>

      {liste.isLoading ? (
        <p className={styles.vide} aria-busy="true">
          {t('commun:chargement')}
        </p>
      ) : liste.isError ? (
        <p className={styles.vide} role="alert">
          {estApiError(liste.error) ? liste.error.message : t('commun:erreurReseau')}
        </p>
      ) : factures.length === 0 ? (
        <div className={styles.videFeuille}>
          <span className={styles.feuilleVierge} aria-hidden />
          <p>{t('regie:ventes.aucunResultat')}</p>
        </div>
      ) : (
        <div className={styles.tableauEnveloppe}>
          <table className={styles.tableau} data-density="registre">
            <thead>
              <tr>
                <th scope="col">{t('regie:ventes.colonneNumero')}</th>
                <th scope="col">{t('regie:ventes.colonneDate')}</th>
                <th scope="col">{t('regie:ventes.colonneContact')}</th>
                <th scope="col" className={styles.colMontant}>
                  {t('regie:ventes.colonneHt')}
                </th>
                <th scope="col" className={styles.colMontant}>
                  {t('regie:ventes.colonneTtc')}
                </th>
                <th scope="col">{t('regie:ventes.colonneEtat')}</th>
                <th scope="col">{t('regie:ventes.colonneDocuments')}</th>
              </tr>
            </thead>
            <tbody>
              {factures.map((facture) => (
                <tr key={facture.id} data-etat={facture.etat}>
                  <td className={styles.celNumero}>
                    <Link className={styles.lienLigne} to={`/${galerieSlug}/ventes/${facture.id}`}>
                      <span className={facture.numero ? styles.numero : styles.numeroBrouillon}>
                        {facture.numero ?? t('ventes:factures.brouillon')}
                      </span>
                      {facture.type === 'avoir' ? (
                        <Badge tone="neutral">{t('ventes:factures.avoir')}</Badge>
                      ) : null}
                    </Link>
                  </td>
                  <td className={styles.celDate}>
                    {facture.emiseLe ? formatDate(new Date(facture.emiseLe), langue, 'court') : '—'}
                  </td>
                  <td className={styles.celContact}>{facture.contactNom ?? '—'}</td>
                  <td
                    className={`${styles.colMontant} ${styles.celHt}`}
                    data-libelle={t('regie:ventes.colonneHt')}
                  >
                    <Price
                      cents={facture.totaux.totalHtCentimes}
                      lang={langue}
                      currency={facture.devise}
                    />
                  </td>
                  <td className={`${styles.colMontant} ${styles.celTtc}`}>
                    <Price
                      cents={facture.totaux.totalTtcCentimes}
                      lang={langue}
                      currency={facture.devise}
                    />
                  </td>
                  <td className={styles.celEtat}>
                    <Badge tone={teinteDeLEtatFacture(facture.etat)}>
                      {t(`ventes:factures.${facture.etat}`)}
                    </Badge>
                  </td>
                  <td className={styles.celDocuments}>
                    <span className={styles.documents}>
                      {facture.pdfDisponible ? (
                        <span className={styles.pastilleDoc}>
                          <FileText
                            size={14}
                            strokeWidth={1.5}
                            aria-label={t('regie:ventes.pdfDisponible')}
                          />
                        </span>
                      ) : null}
                      {facture.facturXDisponible ? (
                        <span className={styles.facturX}>Factur-X</span>
                      ) : null}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {liste.data && liste.data.pagination.pages > 1 ? (
        <div className={styles.pagination}>
          <IconButton
            variant="outline"
            icon={<ChevronLeft aria-hidden />}
            aria-label={t('regie:ventes.pagination.precedent')}
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((valeur) => Math.max(1, valeur - 1))}
          />
          <span>
            {t('regie:ventes.pagination.page', {
              page: liste.data.pagination.page,
              pages: liste.data.pagination.pages,
            })}
          </span>
          <IconButton
            variant="outline"
            icon={<ChevronRight aria-hidden />}
            aria-label={t('regie:ventes.pagination.suivant')}
            size="sm"
            disabled={page >= liste.data.pagination.pages}
            onClick={() => setPage((valeur) => Math.min(liste.data!.pagination.pages, valeur + 1))}
          />
        </div>
      ) : null}
    </div>
  );

  return (
    <div className={styles.enveloppe}>
      <div className={styles.entete}>
        <h1 className={styles.titre}>{t('ventes:titre')}</h1>
        <Verrouillable action="invoices:write" ouvert={peutFacturer}>
          <Button
            size="sm"
            onClick={() => {
              const suivants = new URLSearchParams(searchParams);
              suivants.set('nouvelle', '1');
              setSearchParams(suivants);
            }}
          >
            <Plus size={16} strokeWidth={1.5} aria-hidden />
            {t('regie:ventes.nouvelle.bouton')}
          </Button>
        </Verrouillable>
      </div>

      {peutReglerFacturation ? (
        <Tabs
          // Contrôlé par `?onglet=` (même schéma que ContactsPage) : Réglages
          // > « Facturation » renvoie directement vers cet onglet (ARTO-53).
          value={searchParams.get('onglet') === 'reglages' ? 'reglages' : 'factures'}
          onValueChange={(valeur) => {
            const suivants = new URLSearchParams(searchParams);
            if (valeur === 'reglages') suivants.set('onglet', 'reglages');
            else suivants.delete('onglet');
            setSearchParams(suivants);
          }}
          tabs={[
            { value: 'factures', label: t('ventes:factures.titre'), content: contenuFactures },
            {
              value: 'reglages',
              label: t('regie:ventes.reglages.titre'),
              content: <ReglagesFacturationTab galerieSlug={galerieSlug} galerie={galerie} />,
            },
          ]}
        />
      ) : (
        contenuFactures
      )}

      <NouvelleFactureDialog
        key={nouvelleOuverte ? 'nouvelle-ouverte' : 'nouvelle-fermee'}
        galerieSlug={galerieSlug}
        open={nouvelleOuverte}
        onOpenChange={(ouvert) => {
          if (!ouvert) fermerNouvelle();
        }}
        onCreee={(id) => {
          fermerNouvelle();
          navigate(`/${galerieSlug}/ventes/${id}`);
        }}
      />
    </div>
  );
}
